/**
 * Institution Settings Page
 * Allows superadmin to view and change the institution type
 */

import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchInstitutionSettings,
  fetchInstitutionTypes,
  updateInstitutionType,
  selectInstitutionSettings,
  selectTypesInfo,
  selectInstitutionLoading,
  selectInstitutionError,
  clearError
} from '../../redux/slices/institutionSlice';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Badge,
  ListGroup,
  Spinner
} from 'react-bootstrap';
import { BsBuilding, BsClockHistory, BsInfoCircle } from 'react-icons/bs';

const InstitutionSettings = () => {
  const dispatch = useDispatch();
  const settings = useSelector(selectInstitutionSettings);
  const typesInfo = useSelector(selectTypesInfo);
  const loading = useSelector(selectInstitutionLoading);
  const error = useSelector(selectInstitutionError);

  const [selectedType, setSelectedType] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  // Fetch settings and types on mount
  useEffect(() => {
    dispatch(fetchInstitutionSettings());
    dispatch(fetchInstitutionTypes());
  }, [dispatch]);

  // Update selected type when settings load
  useEffect(() => {
    if (settings) {
      setSelectedType(settings.institution_type);
    }
  }, [settings]);

  // Auto-clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if there's any change
    if (selectedType === settings?.institution_type) {
      return;
    }

    // Confirm before updating
    const typeLabel = typesInfo.find(t => t.value === selectedType)?.label || selectedType;
    const confirmed = window.confirm(
      `Are you sure you want to change the institution type to "${typeLabel}"?\n\n` +
      `This will affect which features are available system-wide.`
    );

    if (!confirmed) {
      return;
    }

    try {
      await dispatch(updateInstitutionType(selectedType)).unwrap();
      setShowSuccess(true);

      // Hide success message after 5 seconds
      setTimeout(() => setShowSuccess(false), 5000);

      // Reload page after 2 seconds to refresh sidebar and apply changes
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      // Error is handled by Redux and displayed via error state
    }
  };

  const getSelectedTypeInfo = () => {
    return typesInfo.find(t => t.value === selectedType);
  };

  if (loading && !settings) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3">Loading institution settings...</p>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Row>
        <Col md={12}>
          <h2 className="mb-4">
            <BsBuilding className="me-2" />
            Institution Settings
          </h2>
        </Col>
      </Row>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => dispatch(clearError())}>
          <strong>Error:</strong> {error}
        </Alert>
      )}

      {/* Success Alert */}
      {showSuccess && (
        <Alert variant="success" dismissible onClose={() => setShowSuccess(false)}>
          <strong>Success!</strong> Institution type updated successfully. Page will reload...
        </Alert>
      )}

      <Row>
        <Col md={8}>
          {/* Main Settings Card */}
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Current Institution Type</h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Institution Type</Form.Label>
                  <Form.Select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    disabled={loading}
                  >
                    {typesInfo.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </Form.Select>
                  {getSelectedTypeInfo() && (
                    <Form.Text className="text-muted">
                      {getSelectedTypeInfo().description}
                    </Form.Text>
                  )}
                </Form.Group>

                <Alert variant="info">
                  <BsInfoCircle className="me-2" />
                  <strong>Note:</strong> Changing the institution type will affect which features
                  are available throughout the system. Only features relevant to the selected type
                  will be displayed in menus and accessible via API.
                </Alert>

                <Button
                  type="submit"
                  variant="primary"
                  disabled={loading || selectedType === settings?.institution_type}
                >
                  {loading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Updating...
                    </>
                  ) : (
                    'Update Institution Type'
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>

          {/* Available Types Card */}
          <Card>
            <Card.Header>
              <h5 className="mb-0">Available Institution Types</h5>
            </Card.Header>
            <Card.Body>
              <ListGroup variant="flush">
                {typesInfo.map((type) => (
                  <ListGroup.Item key={type.value}>
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <strong>{type.label}</strong>
                        {type.value === settings?.institution_type && (
                          <Badge bg="success" className="ms-2">Current</Badge>
                        )}
                        <p className="mb-0 text-muted small mt-1">{type.description}</p>
                      </div>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          {/* Change History Card */}
          <Card className="mb-3">
            <Card.Header>
              <h5 className="mb-0">
                <BsClockHistory className="me-2" />
                Change History
              </h5>
            </Card.Header>
            <Card.Body>
              {settings?.change_history && settings.change_history.length > 0 ? (
                <ListGroup variant="flush">
                  {settings.change_history.slice(-10).reverse().map((change, idx) => (
                    <ListGroup.Item key={idx} className="px-0">
                      <div className="small">
                        <div className="mb-1">
                          <Badge bg="secondary" className="me-2">
                            {change.old_value}
                          </Badge>
                          →
                          <Badge bg="primary" className="ms-2">
                            {change.new_value}
                          </Badge>
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                          {new Date(change.changed_at).toLocaleString('en-GB')}
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                          By: {change.changed_by}
                        </div>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              ) : (
                <p className="text-muted mb-0">No change history available</p>
              )}
            </Card.Body>
          </Card>

          {/* Current Settings Card */}
          <Card>
            <Card.Header>
              <h5 className="mb-0">Current Settings</h5>
            </Card.Header>
            <Card.Body>
              <dl className="row mb-0">
                <dt className="col-sm-6">Type:</dt>
                <dd className="col-sm-6">
                  <Badge bg="primary">{settings?.institution_type}</Badge>
                </dd>

                <dt className="col-sm-6">Last Updated:</dt>
                <dd className="col-sm-6 small">
                  {settings?.updated_at
                    ? new Date(settings.updated_at).toLocaleString('en-GB')
                    : 'N/A'}
                </dd>

                <dt className="col-sm-6">Updated By:</dt>
                <dd className="col-sm-6 small">{settings?.updated_by || 'N/A'}</dd>
              </dl>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default InstitutionSettings;
